import { createFileRoute, useNavigate, Navigate, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";

import type { CommunityTemplate } from "@/lib/community-templates";
import { COMMUNITY_TEMPLATES } from "@/lib/community-templates";

import { CreateGroupForm } from "@/components/groups/create-group-form";
import { AppPageContent } from "@/components/layout/app-page";
import { Button } from "@/components/ui/button";
import { SectionCard } from "@/components/ui/section-card";
import { useGroupLabel } from "@/hooks/use-group-label";
import { cn } from "@/lib/utils";
import { useCurrentUser } from "@/hooks/use-current-account";

export const Route = createFileRoute("/_authenticated/app/groups/new")({
  component: CreateGroupPage,
});

function CreateGroupPage() {
  const navigate = useNavigate();
  const groupLabel = useGroupLabel();
  const { data: currentUser } = useCurrentUser();
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedTemplate, setSelectedTemplate] = useState<CommunityTemplate | null>(null);

  const canCreateGroups =
    currentUser?.role === "company_admin" || currentUser?.role === "rlg_admin";

  if (currentUser === undefined) {
    return (
      <AppPageContent variant="medium">
        <p className="py-6 text-sm text-muted-foreground">Loading…</p>
      </AppPageContent>
    );
  }

  if (!canCreateGroups) {
    return <Navigate to="/app/groups" replace />;
  }

  if (step === 1) {
    return (
      <AppPageContent variant="medium">
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 mb-4"
            render={<Link to="/app/groups" />}
            nativeButton={false}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to {groupLabel.plural}
          </Button>
          <h1 className="text-2xl font-bold sm:text-3xl">Choose a Template</h1>
          <p className="mt-1.5 text-muted-foreground">
            Start with a preset to set up your {groupLabel.singularLower} faster, or pick Blank for
            a custom setup.
          </p>
        </div>

        <div
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
          style={{ gridAutoRows: "1fr" }}
        >
          {COMMUNITY_TEMPLATES.map((template) => {
            const Icon = template.Icon;
            const isSelected = selectedTemplate?.id === template.id;
            return (
              <button
                key={template.id}
                type="button"
                onClick={() => setSelectedTemplate(template)}
                className={cn(
                  "flex h-full cursor-pointer flex-col gap-3 rounded-lg border p-4 text-left transition-colors",
                  isSelected
                    ? "border-primary bg-primary/5"
                    : "hover:bg-accent hover:border-border",
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
                    <Icon className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <span className="font-semibold leading-tight">{template.name}</span>
                </div>
                <p className="text-sm text-muted-foreground">{template.description}</p>
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium">Recommended for:</span> {template.recommendedFor}
                </p>
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            className="w-40"
            disabled={!selectedTemplate}
            onClick={() => {
              if (selectedTemplate) setStep(2);
            }}
          >
            Next
          </Button>
        </div>
      </AppPageContent>
    );
  }

  return (
    <AppPageContent className="max-w-168.75">
      <div className="mb-6">
        <Button variant="ghost" size="sm" className="-ml-2 mb-4" onClick={() => setStep(1)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to templates
        </Button>
        <h1 className="text-2xl font-bold sm:text-3xl">Create {groupLabel.singular}</h1>
        <p className="mt-1.5 text-muted-foreground">
          Create a new {groupLabel.singularLower} for your team. You will be added as the{" "}
          {groupLabel.singularLower} admin.
        </p>
      </div>

      <SectionCard>
        <CreateGroupForm
          userRole={currentUser.role}
          template={selectedTemplate ?? undefined}
          onCreated={(groupId) => {
            void navigate({ to: "/app/groups/$groupId", params: { groupId } });
          }}
          onBack={() => setStep(1)}
        />
      </SectionCard>
    </AppPageContent>
  );
}
